import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getVisitsSummary, getAlertStats, getActiveVisits } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  ClipboardCheck,
  AlertTriangle,
  Clock,
  Package,
  TrendingUp,
  Users,
  Store,
  Bell
} from 'lucide-react';

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [alertStats, setAlertStats] = useState(null);
  const [activeVisits, setActiveVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [summaryRes, alertsRes, activeRes] = await Promise.all([
        getVisitsSummary({}),
        getAlertStats(),
        getActiveVisits()
      ]);
      setStats(summaryRes.data);
      setAlertStats(alertsRes.data);
      setActiveVisits(activeRes.data);
    } catch (error) {
      toast.error('Error cargando dashboard');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, variant = 'default', description }) => (
    <Card className="border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${
          variant === 'success' ? 'text-emerald-600' :
          variant === 'warning' ? 'text-amber-600' :
          variant === 'danger' ? 'text-red-600' :
          'text-slate-400'
        }`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-slate-900 font-['Chivo']">{value}</div>
        {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Dashboard</h1>
        <p className="text-slate-500">Bienvenido, {user?.name}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Visitas Totales"
          value={stats?.total_visits || 0}
          icon={ClipboardCheck}
          description="Últimos 7 días"
        />
        <StatCard
          title="Completadas"
          value={`${stats?.completion_rate || 0}%`}
          icon={TrendingUp}
          variant="success"
          description={`${stats?.completed || 0} visitas`}
        />
        <StatCard
          title="En Progreso"
          value={stats?.in_progress || 0}
          icon={Clock}
          variant="warning"
        />
        <StatCard
          title="Alertas Pendientes"
          value={alertStats?.pending || 0}
          icon={Bell}
          variant={alertStats?.pending > 0 ? 'danger' : 'default'}
        />
      </div>

      {/* Alert Stats */}
      {alertStats && alertStats.pending > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-['Chivo']">Alertas por Severidad</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-600" />
                <span className="text-sm text-slate-600">Críticas: {alertStats.by_severity?.critical || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-sm text-slate-600">Advertencias: {alertStats.by_severity?.warning || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-slate-600">Info: {alertStats.by_severity?.info || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Visits */}
      {activeVisits.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-['Chivo']">Visitas Activas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeVisits.slice(0, 5).map((visit) => (
                <div 
                  key={visit.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-slate-900">{visit.store_name}</p>
                    <p className="text-sm text-slate-500">{visit.user_name}</p>
                  </div>
                  <Badge variant={visit.status === 'EN_PROGRESO' ? 'default' : 'secondary'}>
                    {visit.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Low Stock Incidents */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-['Chivo']">Incidentes de Bajo Stock</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center">
              <Package className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900 font-['Chivo']">
                {stats?.low_stock_incidents || 0}
              </p>
              <p className="text-sm text-slate-500">Reportes de bajo stock esta semana</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
